import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatchApp } from "../../../lib/redux";
import { startGetGlobalReportGoalsProjection } from "../../../actions/adsCampaigns";
import { useReportSnapshot } from "../../../features/reportPdf/ReportSnapshotContext";
import { GLOBAL_REPORT_GOALS_PROJECTION_FORECAST_DAYS } from "../utils/getGlobalReportGoalsDateRanges";

const buildProjectionRequestKey = ({
  startDateTime,
  endDateTime,
  historicalDaily,
}) => {
  if (!startDateTime || !endDateTime || !historicalDaily?.length) {
    return null;
  }

  const seriesKey = historicalDaily
    .map((row) => `${row.date}:${row.results}`)
    .join("|");

  return `${startDateTime}|${endDateTime}|${seriesKey}`;
};

const useGlobalReportGoalsAiProjection = ({
  historicalDaily = [],
  hasHistoricalActivity = false,
  startDateTime,
  endDateTime,
  isStatisticsSettledForRange = false,
  isLoadingStatistics = false,
  forecastDays = GLOBAL_REPORT_GOALS_PROJECTION_FORECAST_DAYS,
  enabled = true,
}) => {
  const dispatch = useDispatchApp();
  const snapshot = useReportSnapshot();
  const [forecastDailyResults, setForecastDailyResults] = useState(null);
  const [isProjectionLoading, setIsProjectionLoading] = useState(false);
  const [projectionError, setProjectionError] = useState(null);
  const lastFetchedKeyRef = useRef(null);

  const requestKey = useMemo(
    () =>
      buildProjectionRequestKey({
        startDateTime,
        endDateTime,
        historicalDaily,
      }),
    [endDateTime, historicalDaily, startDateTime]
  );

  const shouldFetch =
    enabled &&
    isStatisticsSettledForRange &&
    !isLoadingStatistics &&
    hasHistoricalActivity &&
    historicalDaily.length > 0 &&
    !!requestKey;

  useEffect(() => {
    if (snapshot) {
      return undefined;
    }

    if (!enabled) {
      setForecastDailyResults(null);
      setIsProjectionLoading(false);
      setProjectionError(null);
      lastFetchedKeyRef.current = null;
      return undefined;
    }

    if (!shouldFetch) {
      // Keep last successful forecast while stats briefly reload — avoids
      // goals-settled flicker that restarts PDF chart prewarm.
      setIsProjectionLoading(false);
      return undefined;
    }

    if (lastFetchedKeyRef.current === requestKey) {
      return undefined;
    }

    const abortController = new AbortController();
    let isActive = true;

    setIsProjectionLoading(true);
    setProjectionError(null);

    const fetchProjection = async () => {
      const response = await dispatch(
        startGetGlobalReportGoalsProjection({
          historicalDaily,
          forecastDays,
          signal: abortController.signal,
        })
      );

      if (!isActive || abortController.signal.aborted) {
        return;
      }

      if (response?.ok && response.data?.forecastDailyResults) {
        lastFetchedKeyRef.current = requestKey;
        setForecastDailyResults(response.data.forecastDailyResults);
        setProjectionError(null);
      } else if (!response?.aborted) {
        lastFetchedKeyRef.current = requestKey;
        setForecastDailyResults(null);
        setProjectionError(new Error("projection_failed"));
      }

      setIsProjectionLoading(false);
    };

    fetchProjection();

    return () => {
      isActive = false;
      abortController.abort();
    };
  }, [
    dispatch,
    enabled,
    forecastDays,
    historicalDaily,
    requestKey,
    shouldFetch,
    snapshot,
  ]);

  if (snapshot) {
    return {
      forecastDailyResults: snapshot.goalsForecastDailyResults ?? null,
      isProjectionLoading: false,
      projectionError: null,
    };
  }

  return {
    forecastDailyResults,
    isProjectionLoading: shouldFetch && isProjectionLoading,
    projectionError,
  };
};

export default useGlobalReportGoalsAiProjection;
